(() => {
  'use strict';
  if (customElements.get('dmr-product-proof')) return;
  const instances = new Set();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let wanted = null;
  const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
  const allVideos = () => [...instances].flatMap(section => section.videos || []);
  const load = video => {
    if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
  };
  function sync() {
    frame = 0;
    wanted = null;
    let bestScore = -Infinity;
    const blocked = document.hidden || document.querySelector('dialog[open],.cc-popup.cc-popup--visible,.modal--visible,.loox-overlay');
    if (!blocked) for (const section of instances) {
      if (!section.track) continue;
      const track = section.track.getBoundingClientRect();
      for (const video of section.videos) {
        if (section.userPaused.has(video) || section.failed.has(video)) continue;
        const r = video.getBoundingClientRect();
        const height = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
        const width = Math.max(0, Math.min(r.right, track.right, innerWidth) - Math.max(r.left, track.left, 0));
        if (r.width < 1 || r.height < 1 || width < r.width * .65 || height < Math.min(r.height * .5, 180)) continue;
        const manual = section.forced === video;
        if (!manual && (reduced.matches || navigator.connection?.saveData)) continue;
        const score = height * width / (r.height * r.width) * 1000 - Math.abs((r.left + r.right - track.left - track.right) / 2) * .1 + (manual ? 10000 : 0);
        if (score > bestScore) { wanted = video; bestScore = score; }
      }
    }
    for (const video of allVideos()) if (video !== wanted) video.pause();
    if (wanted && wanted.paused) {
      const next = wanted;
      load(next);
      next.muted = true;
      const pending = next.play();
      pending?.then(() => { if (wanted !== next || document.hidden) next.pause(); }).catch(() => {});
    }
  }
  class DmrProductProof extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('[data-dmr-proof-track]');
      this.cards = [...this.querySelectorAll('[data-dmr-proof-card]')];
      this.videos = [...this.querySelectorAll('[data-dmr-proof-video]')];
      this.mediaCards = this.cards.filter(card => card.dataset.kind !== 'review');
      this.userPaused = new Set();
      this.failed = new Set();
      this.forced = null;
      this.dialog = this.querySelector('[data-dmr-proof-dialog]');
      this.fullVideo = this.querySelector('[data-dmr-proof-full-video]');
      this.fullImage = this.querySelector('[data-dmr-proof-full-image]');
      this.modalIndex = 0;
      instances.add(this);
      if (!this.track) return;
      this.on(this, 'click', event => {
        const review = event.target.closest('[data-dmr-proof-review]');
        if (review && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && review.dataset.reviewId && typeof window.LOOX?.openQV === 'function') {
          try {
            window.LOOX.openQV(review.dataset.reviewId, null, document.documentElement.lang);
            event.preventDefault();
            document.querySelectorAll('video').forEach(video => video.pause());
            schedule();
          } catch (_) {}
        }
        const step = event.target.closest('[data-dmr-proof-step]');
        if (step) this.move(Number(step.dataset.dmrProofStep));
        const toggle = event.target.closest('[data-dmr-proof-toggle]');
        if (toggle) {
          const video = toggle.closest('[data-dmr-proof-card]').querySelector('video');
          if (!video.paused) { this.userPaused.add(video); this.forced = null; video.pause(); }
          else { this.userPaused.delete(video); this.failed.delete(video); this.forced = video; }
          schedule();
        }
        const open = event.target.closest('[data-dmr-proof-open]');
        if (open && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.modalIndex = this.mediaCards.indexOf(open.closest('[data-dmr-proof-card]'));
          this.opener = open;
          this.dialog.showModal();
          this.showMedia();
          schedule();
        }
        if (event.target.closest('[data-dmr-proof-close]')) this.dialog.close();
        const modalStep = event.target.closest('[data-dmr-proof-modal-step]');
        if (modalStep) this.moveModal(Number(modalStep.dataset.dmrProofModalStep));
      });
      this.on(this.track, 'scroll', () => { this.navigation(); schedule(); }, { passive: true });
      this.on(this.track, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      this.on(this.dialog, 'close', () => { this.stopFull(); this.opener?.focus({ preventScroll: true }); schedule(); });
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      });
      this.on(this.dialog, 'keydown', event => {
        if (event.target === this.fullVideo || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.moveModal(event.key === 'ArrowLeft' ? -1 : 1);
      });
      for (const video of this.videos) {
        video.muted = true;
        const button = video.closest('[data-dmr-proof-card]').querySelector('[data-dmr-proof-toggle]');
        const paint = () => {
          button.setAttribute('aria-label', video.paused ? this.dataset.play : this.dataset.pause);
          button.setAttribute('aria-pressed', String(!video.paused));
          button.querySelector('[data-dmr-proof-toggle-icon]').textContent = video.paused ? '▶' : 'Ⅱ';
        };
        this.on(video, 'play', paint); this.on(video, 'pause', paint);
        this.on(video, 'error', () => { this.failed.add(video); paint(); });
        paint();
      }
      this.on(document, 'shopify:block:select', event => {
        const card = event.target.closest('[data-dmr-proof-card]');
        if (card && this.contains(card)) this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'auto' });
      });
      this.resizeObserver = new ResizeObserver(() => { this.navigation(); schedule(); });
      this.resizeObserver.observe(this.track);
      this.navigation();
      schedule();
    }
    on(target, name, callback, options = {}) { target?.addEventListener(name, callback, { ...options, signal: this.controller.signal }); }
    navigation() {
      if (!this.track) return;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 3;
      this.querySelector('[data-dmr-proof-step="-1"]').disabled = this.track.scrollLeft <= 3;
      this.querySelector('[data-dmr-proof-step="1"]').disabled = atEnd;
    }
    move(step) {
      const first = this.cards[0];
      if (!first) return;
      const width = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      this.track.scrollBy({ left: step * width, behavior: reduced.matches ? 'auto' : 'smooth' });
    }
    showMedia() {
      const card = this.mediaCards[this.modalIndex];
      if (!card) return;
      allVideos().forEach(video => video.pause());
      this.stopFull();
      const isVideo = card.dataset.kind === 'video';
      this.fullVideo.hidden = !isVideo;
      this.fullImage.hidden = isVideo;
      if (isVideo) {
        this.fullVideo.src = card.dataset.fullSrc;
        this.fullVideo.poster = card.dataset.poster || '';
        this.fullVideo.setAttribute('aria-label', card.dataset.alt || '');
        this.fullVideo.muted = true;
        this.fullVideo.play().catch(() => {});
      } else {
        this.fullImage.src = card.dataset.fullSrc;
        this.fullImage.alt = card.dataset.alt || '';
      }
      this.querySelector('[data-dmr-proof-count]').textContent = `${this.modalIndex + 1} / ${this.mediaCards.length}`;
      this.querySelectorAll('[data-dmr-proof-modal-step]').forEach(button => button.hidden = this.mediaCards.length < 2);
    }
    moveModal(step) {
      if (!this.mediaCards.length) return;
      this.modalIndex = (this.modalIndex + step + this.mediaCards.length) % this.mediaCards.length;
      this.showMedia();
    }
    stopFull() {
      if (!this.fullVideo) return;
      this.fullVideo.pause(); this.fullVideo.removeAttribute('src'); this.fullVideo.load();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.resizeObserver?.disconnect();
      this.videos?.forEach(video => video.pause()); this.stopFull();
      if (this.dialog?.open) this.dialog.close();
      instances.delete(this); this.controller = null; schedule();
    }
  }
  document.addEventListener('play', event => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    if (event.target.closest('dmr-product-proof')) {
      document.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
    } else allVideos().forEach(video => video.pause());
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) for (const section of instances) section.fullVideo?.pause();
    schedule();
  });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  reduced.addEventListener('change', schedule);
  new MutationObserver(schedule).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
  customElements.define('dmr-product-proof', DmrProductProof);
})();
